import React, { useMemo, useState } from 'react'
import { X } from 'lucide-react'
import {
  buildCreateNamesFromSelectionDialogPresentation,
  normalizeCreateNamesFromSelectionOptions,
  toggleCreateNamesFromSelectionOption,
  validateCreateNamesFromSelectionOptions,
  type CreateNamesFromSelectionOptions,
} from '../../spreadsheet/definedNamesPresentation'
import './ExcelWorkbookChrome.css'

export type ExcelCreateNamesFromSelectionOptions = CreateNamesFromSelectionOptions

type ExcelCreateNamesFromSelectionDialogProps = {
  selectionLabel: string
  defaultOptions?: Partial<ExcelCreateNamesFromSelectionOptions>
  onClose: () => void
  onCreate: (options: ExcelCreateNamesFromSelectionOptions) => void | Promise<void>
}

export function ExcelCreateNamesFromSelectionDialog({
  selectionLabel,
  defaultOptions,
  onClose,
  onCreate,
}: ExcelCreateNamesFromSelectionDialogProps) {
  const initialOptions = useMemo<ExcelCreateNamesFromSelectionOptions>(
    () => normalizeCreateNamesFromSelectionOptions(defaultOptions),
    [defaultOptions],
  )
  const [options, setOptions] = useState<ExcelCreateNamesFromSelectionOptions>(initialOptions)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const presentation = useMemo(() => buildCreateNamesFromSelectionDialogPresentation({
    selectionLabel,
    options,
  }), [options, selectionLabel])

  const toggle = (key: keyof ExcelCreateNamesFromSelectionOptions) => {
    setOptions((current) => toggleCreateNamesFromSelectionOption(current, key))
    setError(null)
  }

  const commit = async () => {
    const validationError = validateCreateNamesFromSelectionOptions(options)
    if (validationError) {
      setError(validationError)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await Promise.resolve(onCreate(options))
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" onMouseDown={onClose}>
      <section
        className="fg-excel-create-names-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={presentation.ariaLabel}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="fg-excel-format-dialog-header">
          <strong>{presentation.title}</strong>
          <button type="button" className="fg-excel-icon-button" aria-label={presentation.closeAriaLabel} title={presentation.closeTitle} onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="fg-excel-create-names-body">
          <div className="fg-excel-create-names-selection" title={presentation.selectionTitle}>{presentation.selectionLabel}</div>
          <fieldset>
            <legend>{presentation.optionsLegend}</legend>
            {presentation.options.map((option) => (
              <label key={option.key}>
                <input type="checkbox" checked={option.checked} onChange={() => toggle(option.key)} />
                <span>{option.label}</span>
              </label>
            ))}
          </fieldset>
          {error && <div className="fg-excel-name-manager-error" role="alert">{error}</div>}
        </div>

        <footer className="fg-excel-cell-metadata-footer">
          <button type="button" className="fg-excel-cell-metadata-button" onClick={onClose} disabled={busy}>{presentation.cancelLabel}</button>
          <button type="button" className="fg-excel-cell-metadata-button fg-excel-cell-metadata-button-primary" onClick={commit} disabled={busy}>
            {presentation.createLabel}
          </button>
        </footer>
      </section>
    </div>
  )
}
